import { ChangeDetectionStrategy, ChangeDetectorRef, Component, effect, ElementRef, EventEmitter, inject, OnInit, Output, ViewChild } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { WikiContentService } from '../../services/wiki/wiki-content.service';
import { WikiPanelService } from '../../services/wiki/wiki-panel.service';
import { SetupAttentionService } from '../../services/setup/setup-attention.service';
import { UserProfileStore } from '../../states/user-profile/user-profile.store';
import { WikiNavGroup, WikiTopicContent } from '../../models/wiki/wiki-content.model';

@Component({
  selector: 'app-wiki-panel',
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './wiki-panel.component.html',
  styleUrl: './wiki-panel.component.scss',
})
export class WikiPanelComponent implements OnInit {
  @Output() closePanel = new EventEmitter<void>();
  @ViewChild('contentArea') contentAreaRef!: ElementRef<HTMLElement>;

  private readonly wikiPanelService = inject(WikiPanelService);
  private readonly wikiContent = inject(WikiContentService);
  private readonly setupAttentionService = inject(SetupAttentionService);
  private readonly userProfileStore = inject(UserProfileStore);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly sanitizer = inject(DomSanitizer);

  selectedTopicId  = 'introduction';
  activeSection    = '';
  screenshotLoaded = false;
  lightboxSrc: string | null = null;
  searchQuery = '';

  readonly wikiContentService = this.wikiContent;
  readonly setupAttention = this.setupAttentionService;

  constructor() {
    effect(() => {
      this.userProfileStore.userProfile();
      this.wikiContent.loaded();

      if (!this.wikiPanelService.isOpen() || !this.wikiContent.loaded()) {
        return;
      }

      if (!this.isTopicAllowed(this.selectedTopicId)) {
        this.selectTopic(this.defaultTopicId, false);
      }

      const topicId = this.wikiPanelService.consumePendingTopic();
      if (topicId) {
        this.selectTopic(this.ensureAllowedTopic(topicId));
        this.cdr.markForCheck();
      }
    });
  }

  ngOnInit(): void {
    this.wikiContent.ensureLoaded();
  }

  get isEmployeeUser(): boolean {
    return this.userProfileStore.roleName().trim().toLowerCase() === 'employee';
  }

  get visibleNavGroups(): WikiNavGroup[] {
    const groups = this.wikiContent.navGroups();
    if (!this.isEmployeeUser) {
      return groups;
    }

    const allowedGettingStarted = new Set(this.wikiContent.employeeGettingStartedTopicIds());

    return groups
      .filter((group) => group.id === 'getting-started' || group.id === 'employee-portal')
      .map((group) => {
        if (group.id !== 'getting-started') {
          return group;
        }

        return {
          ...group,
          topics: group.topics.filter((topic) => allowedGettingStarted.has(topic.id)),
        };
      });
  }

  get defaultTopicId(): string {
    return this.visibleNavGroups[0]?.topics[0]?.id ?? 'introduction';
  }

  private isTopicAllowed(topicId: string): boolean {
    return this.visibleNavGroups.some((group) =>
      group.topics.some((topic) => topic.id === topicId),
    );
  }

  private ensureAllowedTopic(topicId: string): string {
    return this.isTopicAllowed(topicId) ? topicId : this.defaultTopicId;
  }

  get currentTopic(): WikiTopicContent {
    const topicId = this.ensureAllowedTopic(this.selectedTopicId);
    const topics = this.wikiContent.topics();
    return topics[topicId] ?? topics[this.defaultTopicId] ?? this.fallbackTopic();
  }

  get hasVideoSlot(): boolean {
    return !!this.currentTopic.video;
  }

  get videoEmbedUrl(): SafeResourceUrl | null {
    const raw = this.currentTopic.video?.url?.trim();
    if (!raw) {
      return null;
    }

    const embedUrl = this.toEmbedUrl(raw);
    if (!embedUrl) {
      return null;
    }

    return this.sanitizer.bypassSecurityTrustResourceUrl(embedUrl);
  }

  get videoCaption(): string {
    return this.currentTopic.video?.caption?.trim() || 'Tutorial video coming soon';
  }

  get filteredNavGroups(): WikiNavGroup[] {
    const query = this.searchQuery.trim().toLowerCase();
    if (!query) {
      return this.visibleNavGroups;
    }

    return this.visibleNavGroups
      .map((group) => ({
        ...group,
        topics: group.topics.filter(
          (topic) =>
            topic.label.toLowerCase().includes(query) ||
            group.label.toLowerCase().includes(query),
        ),
      }))
      .filter((group) => group.topics.length > 0);
  }

  get totalTopicCount(): number {
    return this.visibleNavGroups.reduce((sum, group) => sum + group.topics.length, 0);
  }

  onSearchChange(): void {
    this.cdr.markForCheck();
  }

  selectTopic(id: string, scrollToTop = true): void {
    this.selectedTopicId = this.ensureAllowedTopic(id);
    this.activeSection    = '';
    this.screenshotLoaded = false;
    if (scrollToTop && this.contentAreaRef?.nativeElement) {
      this.contentAreaRef.nativeElement.scrollTop = 0;
    }
    this.cdr.markForCheck();
  }

  openSetupChecklist(): void {
    this.setupAttentionService.showModal();
    this.cdr.markForCheck();
  }

  onScreenshotLoad():  void { this.screenshotLoaded = true; }
  onScreenshotError(): void { this.screenshotLoaded = false; }

  openLightbox():  void { this.lightboxSrc = '/wiki/' + this.selectedTopicId + '.png'; }
  closeLightbox(): void { this.lightboxSrc = null; }

  scrollTo(sectionId: string): void {
    this.activeSection = sectionId;
    const el = document.getElementById(sectionId);
    el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  private fallbackTopic(): WikiTopicContent {
    return {
      title: 'Documentation',
      subtitle: 'Help content is loading.',
      sections: [],
    };
  }

  private toEmbedUrl(url: string): string | null {
    try {
      const parsed = new URL(url);
      const host = parsed.hostname.replace(/^www\./, '');

      if (host === 'youtube.com' || host === 'm.youtube.com') {
        const videoId = parsed.searchParams.get('v');
        return videoId ? `https://www.youtube.com/embed/${videoId}` : null;
      }

      if (host === 'youtu.be') {
        const videoId = parsed.pathname.replace(/^\//, '');
        return videoId ? `https://www.youtube.com/embed/${videoId}` : null;
      }

      if (host === 'youtube.com' && parsed.pathname.startsWith('/embed/')) {
        return url;
      }

      if (host === 'player.vimeo.com') {
        return url;
      }

      if (host === 'vimeo.com') {
        const videoId = parsed.pathname.replace(/^\//, '');
        return videoId ? `https://player.vimeo.com/video/${videoId}` : null;
      }

      return url;
    } catch {
      return null;
    }
  }
}
